// utils/intents/intentPreprocessor.js
// Owns: intent interpretation (LLM fallback)
// Guard: prevents fallback from hijacking clarification / no-specific-selection turns

import { detectIntent, isTriviaQuestion, isTriviaAnswer } from './intentUtils';
import { normalizeRelationshipTerm } from './relationshipIntentUtils';

import { debugLog } from '../logging/debugLog';

const MEMORY_INTENTS = ['memory_recall', 'recall_request'];
const LOSS_INTENTS = ['loss'];

const PLURAL_RELATIONSHIP_TERMS = [
  'daughters','sons','brothers','sisters','friends','coworkers','colleagues',
  'parents','children','wives','husbands','girlfriends','boyfriends',
  'aunts','uncles','cousins','grandmothers','grandfathers','grandmas','grandpas',
];

function normalizeText(value = '') {
  return String(value).toLowerCase().trim();
}

function isMemoryStyleIntent(intent = '') {
  return MEMORY_INTENTS.includes(intent) || LOSS_INTENTS.includes(intent);
}

function isPluralRecallMessage(message = '') {
  const lower = normalizeText(message);
  return PLURAL_RELATIONSHIP_TERMS.some((term) => lower.includes(term));
}

function isNoSpecificSelection(message = '') {
  const lower = normalizeText(message);

  return (
    lower.includes('not specific') ||
    lower.includes('not thinking of one') ||
    lower.includes('not thinking of one specific') ||
    lower.includes('just making sure') ||
    lower.includes('just checking') ||
    lower.includes('not one') ||
    lower.includes('not one specific') ||
    lower.includes('just making sure you know')
  );
}

// 🔥 NEW: clarification override
function isClarificationMessage(message = '') {
  const lower = normalizeText(message);

  return (
    lower.startsWith('i mean') ||
    lower.startsWith('i meant') ||
    lower.startsWith('i was') ||
    lower.startsWith('i was just') ||
    lower.startsWith('no i mean') ||
    lower.startsWith('no i meant') ||
    lower.startsWith('no i was') ||
    lower.includes('i was thanking you') ||
    lower.includes('i was just thanking you')
  );
}

function findExplicitNameMatch(message = '', relationships = []) {
  const lower = normalizeText(message);

  return relationships.find((rel) => {
    const first = normalizeText(rel.first_name);
    const last = normalizeText(rel.last_name);
    const full = [first, last].filter(Boolean).join(' ');

    if (full && lower.includes(full)) return true;
    if (first && lower.includes(first)) return true;

    return false;
  }) || null;
}

function getRoleMatches(target, relationships = []) {
  const normalizedTarget = normalizeRelationshipTerm(target || '');
  if (!normalizedTarget) return [];

  return relationships.filter((rel) => {
    const relRole = normalizeRelationshipTerm(rel.relationship || rel.relationship_type || '');
    return relRole === normalizedTarget;
  });
}

export function preprocessIntent(intentInfo, message, lastTarget, relationships = []) {
  if (!intentInfo) return intentInfo;

  // 🔥 NEW: clarification wins over everything
  if (isClarificationMessage(message)) {
    debugLog('🧠 Clarification detected — suppressing fallback intent');

    return {
      intent: 'clarification',
      target: null,
      memoryName: null,
      source: 'clarification_override',
    };
  }

  // existing guard
  if (isNoSpecificSelection(message)) {
    debugLog('🧠 Blocking fallback target + memoryName due to no-specific-selection');
    intentInfo.target = null;
    intentInfo.source = 'clarification_override';
    return intentInfo;
  }

  // fallback only for real memory-style intents
  if (isMemoryStyleIntent(intentInfo.intent) && !intentInfo.target && lastTarget) {
    intentInfo.target = lastTarget;
  }

  // explicit name wins
  if (isMemoryStyleIntent(intentInfo.intent) && relationships.length) {
    const explicitNameMatch = findExplicitNameMatch(message, relationships);

    if (explicitNameMatch) {
      const resolvedRole = normalizeRelationshipTerm(
        explicitNameMatch.relationship || explicitNameMatch.relationship_type || ''
      );

      if (resolvedRole) {
        intentInfo.target = resolvedRole;
        intentInfo.source = 'explicit_override';
      }
    }
  }

  // clean role override only when singular
  if (
    isMemoryStyleIntent(intentInfo.intent) &&
    relationships.length &&
    intentInfo.target
  ) {
    const roleMatches = getRoleMatches(intentInfo.target, relationships);

    if (roleMatches.length === 1) {
      intentInfo.target = normalizeRelationshipTerm(roleMatches[0].relationship);
    }
  }

  return intentInfo;
}

export function getMemoryName(intentInfo, relationships = [], message = '') {
  if (!intentInfo) return null;

  if (isClarificationMessage(message)) return null;
  if (isNoSpecificSelection(message)) return null;
  if (isPluralRecallMessage(message)) return null;

  if (intentInfo.intent === 'relationship_declaration' && intentInfo.personName) {
    return intentInfo.personName;
  }

  if (!isMemoryStyleIntent(intentInfo.intent) || !relationships.length) {
    return null;
  }

  const explicitNameMatch = findExplicitNameMatch(message, relationships);
  if (explicitNameMatch?.first_name) {
    return explicitNameMatch.first_name;
  }

  if (intentInfo.target) {
    const roleMatches = getRoleMatches(intentInfo.target, relationships).filter((rel) => rel.first_name);

    if (roleMatches.length === 1) {
      return roleMatches[0].first_name;
    }

    return null;
  }

  return null;
}

export async function getIntentInfo(message, lastTarget, relationships = []) {
  const lower = normalizeText(message);


  if (isTriviaQuestion(lower)) {
    return { intent: 'trivia_question', target: null, memoryName: null };
  }

  if (isTriviaAnswer(lower)) {
    return { intent: 'trivia_answer', target: null, memoryName: null };
  }

  const intentInfo = await detectIntent(message);

  if (intentInfo.target) {
    lastTarget = intentInfo.target;
  }

  const preprocessedIntent = preprocessIntent(intentInfo, message, lastTarget, relationships);
  const memoryName = getMemoryName(preprocessedIntent, relationships, message);

  return { ...preprocessedIntent, memoryName };
}